/**
 * Modal component for scheduling or rescheduling content.
 * Allows users to select a date and time for publishing content.
 * Supports both initial scheduling and updating existing schedules.
 * 
 * Example data structure:
 * {
 *   contentId: "uuid",
 *   currentScheduledTime?: "2025-08-15T14:30:00Z",
 *   onScheduleUpdate?: () => void,
 *   isNewSchedule?: boolean
 * }
 */

import { useState, useEffect } from 'react'
import { Calendar, Clock, Globe, AlertTriangle, CheckCircle, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { toast } from '@/components/ui/use-toast'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { supabase } from '@/lib/supabase'
import { useTimezone } from '@/hooks/useTimezone'
import { useBusinessTimezones } from '@/hooks/useTeamTimezones'
import { MultiTimezoneTooltip } from '@/components/timezone/MultiTimezoneTooltip'
import { useContentData } from '../useContentData'

interface ChangeScheduleModalProps {
  isOpen: boolean
  onClose: () => void
  contentId: string
  currentScheduledTime?: string | null
  onScheduleUpdate?: () => void
  isNewSchedule?: boolean // New prop to indicate if this is a new schedule or update
}

export function ChangeScheduleModal({
  isOpen,
  onClose,
  contentId,
  currentScheduledTime,
  onScheduleUpdate,
  isNewSchedule = false
}: ChangeScheduleModalProps) {
  const [newDate, setNewDate] = useState('')
  const [newTime, setNewTime] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [isCanceling, setIsCanceling] = useState(false)
  const [, setCurrentTime] = useState(new Date())
  const [showGlobalTimezones, setShowGlobalTimezones] = useState(false)
  
  // Get scheduling functions from useContentData
  const { createOrUpdateScheduledPost } = useContentData()

  // Get timezone functionality
  const {
    formatDate,
    getCurrentTime,
    fromUTC,
    getDisplayName,
    getAbbreviation,
    isLoading: timezoneLoading
  } = useTimezone()

  // Business timezone conversions for global teams
  const { getBusinessSchedulingRecommendations } = useBusinessTimezones()

  // Update current time every second for live display
  useEffect(() => {
    if (!isOpen) return
    
    const updateCurrentTime = () => {
      setCurrentTime(getCurrentTime())
    }
    
    // Update immediately
    updateCurrentTime()
    
    // Then update every second
    const interval = setInterval(updateCurrentTime, 1000)
    
    return () => clearInterval(interval)
  }, [isOpen, getCurrentTime])

  // Pre-populate form with current scheduled time when modal opens
  useEffect(() => {
    if (isOpen && !timezoneLoading) {
      if (currentScheduledTime) {
        // Convert UTC scheduled time to user's timezone for form display
        const utcDate = new Date(currentScheduledTime)
        const userTimezoneDate = fromUTC(utcDate)
        
        const year = userTimezoneDate.getFullYear()
        const month = String(userTimezoneDate.getMonth() + 1).padStart(2, '0')
        const day = String(userTimezoneDate.getDate()).padStart(2, '0')
        const hours = String(userTimezoneDate.getHours()).padStart(2, '0')
        const minutes = String(userTimezoneDate.getMinutes()).padStart(2, '0')
        
        const dateStr = `${year}-${month}-${day}`
        const timeStr = `${hours}:${minutes}`
        
        setNewDate(dateStr)
        setNewTime(timeStr)
      } else {
        // Default to tomorrow at 9 AM in user's timezone
        const tomorrow = getCurrentTime()
        tomorrow.setDate(tomorrow.getDate() + 1)
        tomorrow.setHours(9, 0, 0, 0)
        
        const year = tomorrow.getFullYear()
        const month = String(tomorrow.getMonth() + 1).padStart(2, '0')
        const day = String(tomorrow.getDate()).padStart(2, '0')
        
        const dateStr = `${year}-${month}-${day}`
        setNewDate(dateStr)
        setNewTime('09:00')
      }
    }
  }, [isOpen, currentScheduledTime, timezoneLoading, fromUTC, getCurrentTime])

  const handleSave = async () => {
    // Debug: confirm click path and current values
    console.log('ChangeScheduleModal.handleSave clicked', { contentId, newDate, newTime, isOpen })
    if (!newDate || !newTime) {
      toast({
        title: "Validation Error",
        description: "Please select both date and time",
        variant: "destructive"
      })
      return
    }

    // Create date in user's timezone then convert to UTC for database storage
    const userTimezoneDateTime = new Date(`${newDate}T${newTime}`)
    
    // Convert to UTC using a more reliable method
    // Create the date as if it's in the user's timezone, then convert to UTC
    const year = userTimezoneDateTime.getFullYear()
    const month = userTimezoneDateTime.getMonth()
    const day = userTimezoneDateTime.getDate()
    const hours = userTimezoneDateTime.getHours()
    const minutes = userTimezoneDateTime.getMinutes()
    
    // Create date components and convert to UTC
    const userTzTime = new Date(year, month, day, hours, minutes)
    const userCurrentTime = getCurrentTime()
    const browserCurrentTime = new Date()
    
    // Calculate the difference between user timezone and browser timezone
    const timezoneOffset = userCurrentTime.getTime() - browserCurrentTime.getTime()
    const utcDateTime = new Date(userTzTime.getTime() - timezoneOffset)
    
    // Validate that the new time is in the future (compare in user's timezone)
    const currentUserTime = getCurrentTime()
    if (userTimezoneDateTime <= currentUserTime) {
      const timezoneAbbr = getAbbreviation()
      toast({
        title: "Invalid Date",
        description: `Please select a future date and time in ${timezoneAbbr}`,
        variant: "destructive"
      })
      return
    }

    setIsLoading(true)

    try {
      // Use the createOrUpdateScheduledPost function for both new and existing schedules
      const result = await createOrUpdateScheduledPost(
        contentId,
        utcDateTime.toISOString()
      )
      
      if (!result.success) {
        throw new Error(result.error || 'Failed to schedule content')
      }

      const formattedTime = formatDate(userTimezoneDateTime, 'MMM dd, yyyy h:mm a')
      const timezoneAbbr = getAbbreviation(userTimezoneDateTime)
      
      toast({
        title: isNewSchedule ? "Content Scheduled" : "Schedule Updated",
        description: `Content ${isNewSchedule ? 'scheduled' : 'rescheduled'} for ${formattedTime} ${timezoneAbbr}`,
      })

      // Call the update callback to refresh parent component data
      onScheduleUpdate?.()
      onClose()
    } catch (error) {
      console.error('Failed to update schedule:', error)
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to update schedule. Please try again.",
        variant: "destructive"
      })
    } finally {
      setIsLoading(false)
    }
  }

  const handleCancelSchedule = async () => {
    if (!currentScheduledTime) {
      toast({
        title: "Error",
        description: "No scheduled post to cancel",
        variant: "destructive"
      })
      return
    }

    setIsCanceling(true)

    try {
      // Update status from 'scheduled' to 'hold' to cancel the post
      const { error } = await supabase
        .from('scheduled_social_posts')
        .update({ 
          status: 'HOLD',
          updated_at: new Date().toISOString()
        })
        .eq('content_id', contentId)
        .eq('status', 'SCHEDULED')

      if (error) {
        console.error('Error canceling schedule:', error)
        throw error
      }

      toast({
        title: "Schedule Cancelled",
        description: "The scheduled post has been cancelled and moved to hold status",
      })

      // Call the update callback to refresh parent component data
      onScheduleUpdate?.()
      onClose()
    } catch (error) {
      console.error('Failed to cancel schedule:', error)
      toast({
        title: "Error",
        description: "Failed to cancel schedule. Please try again.",
        variant: "destructive"
      })
    } finally {
      setIsCanceling(false)
    }
  }

  const handleClose = () => {
    setNewDate('')
    setNewTime('')
    setShowGlobalTimezones(false)
    setIsCanceling(false)
    onClose()
  }

  // Get global timezone analysis for the scheduled time
  const scheduledDateTime = newDate && newTime ? new Date(`${newDate}T${newTime}`) : null
  const globalTimezoneAnalysis = scheduledDateTime ? getBusinessSchedulingRecommendations(scheduledDateTime) : null
  // Inline validation state: selected time must be in the future (user timezone)
  const isSelectionInPast = (() => {
    if (!newDate || !newTime) return false
    try {
      const userSelected = new Date(`${newDate}T${newTime}`)
      const nowUserTz = getCurrentTime()
      return userSelected <= nowUserTz
    } catch {
      return false
    }
  })()

  return (
    <Dialog open={isOpen} onOpenChange={(open) => { if (!open) handleClose() }}>
      <DialogContent className="w-[95vw] max-w-[550px] max-h-[85vh] sm:max-h-[90vh] overflow-y-auto mx-2 sm:mx-4 p-4 sm:p-6">
        <DialogHeader>
          <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3 sm:gap-4">
            <div className="flex-1 min-w-0">
              <DialogTitle className="flex items-center gap-2 text-lg leading-tight">
                <Calendar className="h-5 w-5 shrink-0" />
                {isNewSchedule ? 'Schedule Content' : 'Change Schedule'}
              </DialogTitle>
              <DialogDescription className="mt-2 text-sm leading-relaxed">
                {isNewSchedule 
                  ? 'Select a date and time to publish this content.'
                  : 'Select a new date and time for publishing this content.'}
              </DialogDescription>
            </div>

          </div>
        </DialogHeader>
        
        <div className="space-y-4 sm:space-y-6 py-4 sm:py-6">
          {/* Date Input */}
          <div className="space-y-2">
            <Label htmlFor="date" className="text-sm font-medium flex items-center gap-2">
              <Calendar className="h-4 w-4" />
              Date
            </Label>
            <Input
              id="date"
              type="date"
              value={newDate}
              onChange={(e) => setNewDate(e.target.value)}
              className="w-full h-11 text-base"
              min={getCurrentTime().toISOString().split('T')[0]}
            />
            <p className="text-xs text-muted-foreground">
              Date in your timezone ({getAbbreviation()})
            </p>
          </div>
          
          {/* Time Input */}
          <div className="space-y-2">
            <Label htmlFor="time" className="text-sm font-medium flex items-center gap-2">
              <Clock className="h-4 w-4" />
              <span className="flex-1">
                Time
                {currentScheduledTime && (
                  <span className="block text-xs font-normal text-muted-foreground mt-0.5">
                    Currently: {formatDate(currentScheduledTime, 'MMM dd h:mm a')} {getAbbreviation(new Date(currentScheduledTime))}
                  </span>
                )}
              </span>
            </Label>
            <div className="relative">
              <Input
                id="time"
                type="time"
                value={newTime}
                onChange={(e) => setNewTime(e.target.value)}
                className="w-full h-11 text-base"
              />
            </div>
            <p className="text-xs text-muted-foreground">
              Time will be scheduled in {getDisplayName()} ({getAbbreviation()})
            </p>
            {isSelectionInPast && (
              <p className="text-xs text-destructive mt-1">Please select a future time.</p>
            )}
          </div>

          {/* Preview of scheduled time */}
          {newDate && newTime && (
            <div className="p-3 sm:p-4 bg-muted/20 rounded-lg border border-muted/40">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 text-sm">
                <span className="text-muted-foreground font-medium text-xs sm:text-sm">Will be scheduled for:</span>
                <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                  <span className="font-semibold text-sm break-words">
                    {formatDate(new Date(`${newDate}T${newTime}`), 'MMM dd, yyyy h:mm a')} {getAbbreviation()}
                  </span>
                  <div className="flex items-center gap-2">
                    <MultiTimezoneTooltip
                      date={scheduledDateTime!}
                      showBusinessHours={true}
                      trigger={
                        <Button 
                          size="sm" 
                          variant="ghost"
                          onClick={() => setShowGlobalTimezones(!showGlobalTimezones)}
                          className="h-8 w-8 p-0 hover:bg-muted/50 shrink-0"
                        >
                          <Globe className="h-4 w-4" />
                        </Button>
                      }
                    />
                    <span className="text-xs text-muted-foreground sm:hidden">
                      Global view
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Global timezone analysis */}
          {showGlobalTimezones && scheduledDateTime && globalTimezoneAnalysis && (
            <Card className="border-blue-200 bg-blue-50/30">
              <CardHeader className="pb-2 sm:pb-3">
                <CardTitle className="text-sm flex items-center gap-2">
                  <Globe className="h-4 w-4" />
                  Global Team Impact
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-0 space-y-3 sm:space-y-4">
                {/* Current analysis */}
                <div className="space-y-2">
                  <div className="text-xs font-medium text-muted-foreground">BUSINESS HOURS COVERAGE</div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {globalTimezoneAnalysis.current.slice(0, 6).map((conversion) => (
                      <div key={conversion.timezone} className="flex items-center gap-2 text-xs">
                        {conversion.isBusinessHours ? 
                          <CheckCircle className="h-3 w-3 text-green-500 shrink-0" /> :
                          <Clock className="h-3 w-3 text-gray-400 shrink-0" />
                        }
                        <span className={`break-words ${conversion.isBusinessHours ? 'text-green-700' : 'text-muted-foreground'}`}>
                          {conversion.abbreviation}: {conversion.time.toLocaleTimeString('en-US', { 
                            hour: 'numeric', 
                            minute: '2-digit' 
                          })}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Warnings */}
                {globalTimezoneAnalysis.warnings.length > 0 && (
                  <div className="space-y-2">
                    <div className="text-xs font-medium text-amber-700">COORDINATION CHALLENGES</div>
                    <div className="space-y-1">
                      {globalTimezoneAnalysis.warnings.map((warning, index) => (
                        <div key={index} className="flex items-start gap-2 text-xs text-amber-700">
                          <AlertTriangle className="h-3 w-3 shrink-0 mt-0.5" />
                          <span className="break-words">{warning}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Recommendations */}
                {globalTimezoneAnalysis.recommendations.length > 0 && (
                  <div className="space-y-2">
                    <div className="text-xs font-medium text-muted-foreground">BETTER ALTERNATIVES</div>
                    <div className="space-y-2">
                      {globalTimezoneAnalysis.recommendations.slice(0, 2).map((rec, index) => (
                        <div key={index} className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-3 bg-white rounded-md border text-xs">
                          <div className="flex-1">
                            <div className="font-medium">
                              {rec.time.toLocaleString('en-US', {
                                month: 'short',
                                day: 'numeric',
                                hour: 'numeric',
                                minute: '2-digit'
                              })}
                            </div>
                            <div className="text-muted-foreground mt-1 break-words">{rec.reason}</div>
                          </div>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              const recDate = rec.time
                              const year = recDate.getFullYear()
                              const month = String(recDate.getMonth() + 1).padStart(2, '0')
                              const day = String(recDate.getDate()).padStart(2, '0')
                              const hours = String(recDate.getHours()).padStart(2, '0')
                              const minutes = String(recDate.getMinutes()).padStart(2, '0')
                              
                              setNewDate(`${year}-${month}-${day}`)
                              setNewTime(`${hours}:${minutes}`)
                            }}
                            className="text-xs h-8 px-3 w-full sm:w-auto shrink-0"
                          >
                            Use This Time
                          </Button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </div>
        
        {/* Cancel Schedule Section - only show for existing schedules */}
        {currentScheduledTime && !isNewSchedule && (
          <div className="border-t pt-4">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-3 sm:p-4 bg-destructive/5 rounded-lg border border-destructive/20">
              <div className="flex-1">
                <div className="text-sm font-medium text-destructive">Cancel Scheduled Post</div>
                <div className="text-xs text-muted-foreground mt-1">
                  This will move the content to hold status and prevent it from being published.
                </div>
              </div>
              <Button 
                variant="destructive" 
                onClick={handleCancelSchedule} 
                disabled={isLoading || isCanceling}
                size="default"
                className="w-full sm:w-auto h-11 sm:h-9"
              >
                {isCanceling ? (
                  <>
                    <X className="mr-2 h-4 w-4" />
                    Cancelling...
                  </>
                ) : (
                  <>
                    <X className="mr-2 h-4 w-4" />
                    Cancel Schedule
                  </>
                )}
              </Button>
            </div>
          </div>
        )}

        <DialogFooter className="pt-4">
          <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 w-full">
            <Button 
              type="button"
              variant="outline" 
              onClick={handleClose} 
              disabled={isLoading || isCanceling}
              className="flex-1 h-11 sm:h-10"
            >
              Close
            </Button>
            <Button 
              type="button"
              onClick={(e) => { e.stopPropagation(); handleSave() }} 
              disabled={isLoading || isCanceling || isSelectionInPast || !newDate || !newTime}
              className="flex-1 h-11 sm:h-10"
            >
              {isLoading 
                ? (isNewSchedule ? 'Scheduling...' : 'Updating...') 
                : (isNewSchedule ? 'Schedule Content' : 'Update Schedule')}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}